// Phase 5 (api-phase5.md "Devices"): the open event's speakers and relays, with their live state.
//   load   GET /api/events/:id/devices (speakers, relays, the talk-down library for the site), once per event and
//          again when a drawer opens.
//   live   SSE `device { deviceId, siteId, kind, state, until }` patches the state of this site's devices; a pulse
//          whose `until` has passed reads as off even before the server says so.
//   words  names for device and message ids, so the agent's trace and approvals read "Leave the property on Fence East".

import { useEffect, useMemo, useState } from 'react';
import { api, ApiError, type EventDetail, type EventDevice, type EventDevices, type TalkdownMessage } from '../api.ts';
import type { IconName } from '../components/Icon.tsx';
import { useLoad } from '../hooks.ts';
import { useStreamEvent } from '../stream.tsx';
import { fmtTime } from '../time.ts';
import { human } from './util.ts';

export interface DeviceNames {
  device: (id: string) => string | null;
  /** A speaker's zone (what {zone} becomes), else its name. */
  zone: (id: string) => string | null;
  message: (id: string) => TalkdownMessage | null;
}

export interface DevicesState {
  /** features.deterrence */
  on: boolean;
  /** undefined while loading; null when off or the event has no devices endpoint. */
  data: EventDevices | null | undefined;
  error: unknown;
  reload: () => void;
  /** Speakers and relays with the live state applied. */
  speakers: EventDevice[];
  relays: EventDevice[];
  byId: Map<string, EventDevice>;
  names: DeviceNames;
}

const NO_NAMES: DeviceNames = { device: () => null, zone: () => null, message: () => null };

/** Is the device doing something now (a relay on, a speaker playing)? `until` in the past reads as done. */
export function deviceActive(d: Pick<EventDevice, 'state' | 'until'>, now = Date.now()): boolean {
  if (d.state !== 'on' && d.state !== 'playing') return false;
  const u = d.until ? Date.parse(d.until) : NaN;
  return Number.isNaN(u) || u > now;
}

export function useEventDevices(ev: EventDetail | null, on: boolean): DevicesState {
  const eventId = ev?.id ?? null;
  const siteId = ev?.siteId ?? ev?.site.id ?? null;
  const load = useLoad<EventDevices | null>(
    (sig) =>
      on && eventId
        ? api.devices(eventId, sig).catch((e: unknown) => {
            if (e instanceof ApiError && (e.status === 404 || e.status === 405)) return null;
            throw e;
          })
        : Promise.resolve(null),
    [on, eventId],
  );
  // Live state from the stream, newer than the last load.
  const [live, setLive] = useState<Record<string, { state: string; until: string | null }>>({});
  useEffect(() => setLive({}), [load.data]);
  useStreamEvent('device', (m) => {
    if (!m?.deviceId || (siteId && m.siteId && m.siteId !== siteId)) return;
    setLive((o) => ({ ...o, [m.deviceId]: { state: m.state, until: m.until ?? null } }));
  });
  const data = load.data;
  const merged = useMemo(() => {
    const apply = (d: EventDevice): EventDevice => (live[d.id] ? { ...d, ...live[d.id] } : d);
    const speakers = (data?.speakers ?? []).map(apply);
    const relays = (data?.relays ?? []).map(apply);
    const byId = new Map<string, EventDevice>([...speakers, ...relays].map((d) => [d.id, d]));
    const msgs = new Map((data?.library ?? []).map((m) => [m.id, m]));
    const names: DeviceNames = data
      ? {
          device: (id) => byId.get(id)?.name ?? null,
          zone: (id) => {
            const d = byId.get(id);
            return d ? (d.kind === 'speaker' ? (d.zone ?? d.name) : d.name) : null;
          },
          message: (id) => msgs.get(id) ?? null,
        }
      : NO_NAMES;
    return { speakers, relays, byId, names };
  }, [data, live]);
  const reload = load.reload;
  return useMemo(() => ({ on, data, error: load.error, reload, ...merged }), [on, data, load.error, reload, merged]);
}

/**
 * The speakers to use when the operator has not picked any: the one on the camera in the player, else one whose zone
 * is in that camera's (or the newest alarm camera's) name, else the first.
 */
export function defaultSpeakers(speakers: EventDevice[], ev: EventDetail, cameraId: string | null): string[] {
  const usable = speakers.filter((s) => s.status === 'active' || !s.status);
  const list = usable.length ? usable : speakers;
  if (!list.length) return [];
  const onCam = cameraId ? list.find((s) => s.hostCameraId === cameraId) : undefined;
  if (onCam) return [onCam.id];
  const camNames = [cameraId ? ev.cameras.find((c) => c.id === cameraId)?.name : null, ev.alarms[ev.alarms.length - 1]?.cameraName].filter((x): x is string => !!x).map((x) => x.toLowerCase());
  for (const n of camNames) {
    const z = list.find((s) => s.zone && n.includes(s.zone.toLowerCase()));
    if (z) return [z.id];
  }
  return [list[0]!.id];
}

/** Fills {site} {camera} {zone} {time} the way the server does (site-local time). */
export function fillMessage(text: string, ev: EventDetail, zones: string[]): string {
  const cam = ev.alarms[ev.alarms.length - 1]?.cameraName ?? '';
  return text
    .replaceAll('{site}', ev.site.name)
    .replaceAll('{camera}', cam)
    .replaceAll('{zone}', zones.length ? zones.join(', ') : 'the camera')
    .replaceAll('{time}', fmtTime(Date.now(), ev.siteTimeZone, false));
}

export const CATEGORY_LABEL: Record<string, string> = {
  recording: 'Recording notice',
  leave: 'Leave',
  police: 'Police called',
  warning: 'Warnings',
  custom: 'Custom',
};
export const CATEGORY_ORDER = ['recording', 'leave', 'warning', 'police', 'custom'];

export function categoryLabel(c: string): string {
  return CATEGORY_LABEL[c] ?? human(c);
}

export const FUNCTION_ICON: Record<string, IconName> = {
  strobe: 'bolt',
  siren: 'speaker',
  horn: 'speaker',
  lights: 'spark',
  gate: 'lock',
  lock: 'lock',
  other: 'gear',
};

export function functionIcon(fn: string | null | undefined): IconName {
  return FUNCTION_ICON[fn ?? 'other'] ?? 'gear';
}

export function functionLabel(fn: string | null | undefined): string {
  return fn ? human(fn) : 'Relay';
}

/** Gate and lock relays: a human decision every time (the agent never drives them). */
export function humanOnly(d: Pick<EventDevice, 'function'>, list: readonly string[] = ['gate', 'lock']): boolean {
  return !!d.function && list.includes(d.function);
}
